import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// import { ReactComponent as CloseCircleRedIcon } from '../../assets/icons/close-circle-red.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as EditPencilCard } from '../../assets/icons/edit-pencil.svg';
import { ReactComponent as Plus } from '../../assets/icons/plus.svg';
import { ReactComponent as ChargerIcon } from '../../assets/icons/sidebar/sidebar-device.svg';
// import { ReactComponent as SlashIcon } from '../../assets/icons/slash.svg';
// import { ReactComponent as TickCircleIcon } from '../../assets/icons/tick-circle-icon.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAllDevices,
  fetchDeviceDetails,
  fetchDeviceHubFilters,
  setLimit,
  setPage,
  updateDeviceHubFilters,
  disableDeviceHubFilterApplied,
  setSortData,
  refreshDevices,
} from '../../redux/slices/DeviceHubSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import MyButton from '../../shared-components/button/Button';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { changeDateTimeFormat } from '../../shared-functions/DateTime';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import deviceHubApi from '../../utils/api/device-hub';

import './scss/ev-device-hub.scss';
import ConnectorSidePanel from './components/ConnectorSidePanel';

interface EventDeviceData {
  _id: string;
  brandId: {
    _id: string;
    name: string;
  };
  modelId: {
    _id: string;
    name: string;
  };
  serialNumber: string;
  brandName?: string;
  modelName?: string;
  currentType: 'AC' | 'DC';
  deviceOwnerType: string;
  connectorInformation: {
    connectorType: string;
    connectorFormat: string;
    maxVoltage: number;
    maxCurrent: number;
    connectorRating: number;
    unit: string;
    _id: string;
  }[];
  protocolSupport: string[];
  status: string;
  associatedCPO?: string;
  createdAt: string;
  deviceOwner: string;
  associatedAt?: string;
  tenantName?: string;
  endConsumerName?: string;
  attachedBy?: string;
}

/**
 * Renders the Access Status badge
 * @param {any} row - Table row data
 * @returns {any} JSX element
 */
function renderAccessStatus(row: any) {
  const status = row.status === 'blacklisted' ? 'Blacklisted' : 'Whitelisted';
  const badgeClass = status.toLowerCase() === 'blacklisted' ? 'status-red' : 'status-green';
  return <span className={`device-status-badge ${badgeClass}`}>{status}</span>;
}

/**
 * Renders the Connection Status badge
 * @param {any} row - Table row data
 * @returns {any} JSX element
 */
function renderConnectionStatus(row: any) {
  const rawStatus = row?.connectionStatus ?? (row?.isOnline ? 'online' : 'offline');
  const isOnline = String(rawStatus).toLowerCase() === 'online';
  const displayStatus = isOnline ? 'Online' : 'Offline';
  const badgeClass = isOnline ? 'status-green' : 'status-red';
  return <span className={`device-status-badge ${badgeClass}`}>{displayStatus}</span>;
}

/**
 * Renders the Attached Status badge
 * @param {any} row - Table row data
 * @returns {any} JSX element
 */
function renderAttachedStatus(row: any) {
  const isAttached = row?.deviceOwner && row?.attachedBy;
  const status = isAttached ? 'Attached' : 'Not Attached';
  const badgeClass = isAttached ? 'status-green' : 'status-red';
  return (
    <div className="attached-status-cell">
      <span className={`device-status-badge ${badgeClass}`}>{status}</span>
      {isAttached && row.attachedAt && (
        <span className="date-text">{changeDateTimeFormat(row.attachedAt)}</span>
      )}
    </div>
  );
}

/**
 * Renders the Brand/Model Approval status badge
 * @param {any} row - Status string
 * @returns {any} JSX element
 */
// function renderApprovalStatus(status: string | undefined) {
//   const statusValue = status || 'Unapproved';
//   const statusLower = statusValue.toLowerCase();

//   /**
//    * Returns the appropriate icon based on status
//    * @returns JSX element
//    */
//   const getIcon = () => {
//     switch (statusLower) {
//       case 'approved':
//         return <TickCircleIcon width="14" height="14" />;
//       case 'rejected':
//         return <CloseCircleRedIcon width="14" height="14" />;
//       default:
//         return <SlashIcon width="14" height="14" />;
//     }
//   };

//   return (
//     <div className={`approval-status-badge ${statusLower}`}>
//       {getIcon()}
//       <span className="status-text">{statusValue}</span>
//     </div>
//   );
// }

/**
 * Renders brand name and model stack
 * @param row - row data
 * @returns JSX element
 */
const renderBrandModel = (row: EventDeviceData) => (
  <div className="brand-model-cell">
    <span className="brand-name">{row.brandId?.name || row?.brandName}</span>
    <span className="model-name">{row.modelId?.name || row?.modelName}</span>
  </div>
);

/**
 * Renders device type badge
 * @param {any} row - row data
 * @returns {any} JSX element
 */
const renderType = (row: EventDeviceData) => (
  <span className={`type-badge ${row?.currentType?.toLowerCase()}`}>{row?.currentType}</span>
);

/**
 * Renders protocol support
 * @param {any} row - row data
 * @returns {any} JSX element
 */
const renderProtocol = (row: EventDeviceData) => <span>{row.protocolSupport}</span>;

/**
 * Renders tenant name
 * @param {any} row - row data
 * @returns {any} JSX element
 */
const renderTenant = (row: EventDeviceData) => {
  const ownerType = row.deviceOwnerType === 'commercial' ? 'CPO' : 'eMSP';
  const attachedName = row.attachedBy;

  if (row.deviceOwnerType && attachedName) {
    return <span>{`${ownerType} (${attachedName})`}</span>;
  }
  return <span>-</span>;
};

/**
 * Renders the brand approval status badge
 * @param row - row data
 * @returns JSX element
 */
// const renderBrandStatus = (row: EventDeviceData) => renderApprovalStatus(row.brandStatus);

/**
 * Renders action buttons (edit + delete) for each row
 * @param row - Table row data
 * @returns JSX element
 */

interface EventDeviceHubProperties {
  /** When true, hides the '+ Add Device' button inside the table header */
  hideAddButton?: boolean;
  disablePageTitle?: boolean;
}

/**
 * EventDeviceHub Page Component — Device Hub List
 * @param {any} param0 - The param0
 * @param {any} param0.hideAddButton - The hideAddButton prop
 * @param {any} param0.disablePageTitle - The disablePageTitle prop
 * @returns {any} EventDeviceHub page
 */
function EventDeviceHub({
  hideAddButton = false,
  disablePageTitle = false,
}: EventDeviceHubProperties) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const {
    devices,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    deviceStats,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    filterListLoading,
  } = useAppSelector((state) => state.deviceHub);
  console.log('filters', filters);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  /**
   * Handle delete button click
   * @param {any} id - device ID
   */
  const onDeleteClick = (id: string) => {
    console.log('onDeleteClick called with id:', id);
    setSelectedDeviceId(id);
    setShowDeleteModal(true);
  };

  /**
   * Handle cancel delete
   */
  const handleCancelDelete = () => {
    console.log('handleCancelDelete called');
    setShowDeleteModal(false);
    setSelectedDeviceId(null);
  };

  /**
   * Navigate to Add Device page
   */
  const handleAddDevice = () => {
    dispatch(
      setPageTitle({
        title: '',
        icon: null,
        count: null,
      }),
    );
    navigate('/ev-admin/ev-device-hub/add-device');
  };

  /**
   * Handle confirm delete
   * @param {any} idFromProperties - optional ID from DeleteWarning
   */
  const handleConfirmDelete = async (idFromProperties?: any) => {
    const idToDelete =
      (typeof idFromProperties === 'string' ? idFromProperties : null) || selectedDeviceId;
    console.log('handleConfirmDelete called, idToDelete:', idToDelete);
    if (!idToDelete) return;

    try {
      const response = await deviceHubApi.deleteDevice(idToDelete);
      console.log('Delete API response:', response);
      setNotificationMessage('Device deleted successfully');
      setShowSuccessNotification(true);
      setShowDeleteModal(false);
      setSelectedDeviceId(null);
      // Refresh list and stats
      dispatch(fetchAllDevices({ page, limit }));
      dispatch(fetchDeviceDetails());
    } catch (error: any) {
      console.error('Delete API error:', error);
      setShowDeleteModal(false);
      setSelectedDeviceId(null);
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to delete device',
          status: true,
        },
      });
    }
  };

  /**
   * Renders action buttons (edit + delete) for each row
   * @param {any} row - Table row data
   * @returns {any} JSX element
   */
  const renderActions = (row: EventDeviceData) => {
    const isLinked = row.deviceOwner && row.deviceOwner !== '-';
    const tooltipText =
      'Delete Or Edit Is Available Only When No Associator Are Linked To This Device.';

    const hasUpdatePermission = permissionCheck('devicesEvDeviceHubUpdate');
    const hasDeletePermission = permissionCheck('devicesEvDeviceHubDelete');

    let editTooltip = 'Edit';
    if (!hasUpdatePermission) {
      editTooltip = 'You do not have permission to edit this device.';
    } else if (isLinked) {
      editTooltip = tooltipText;
    }

    let deleteTooltip = 'Delete';
    if (!hasDeletePermission) {
      deleteTooltip = 'You do not have permission to delete this device.';
    } else if (isLinked) {
      deleteTooltip = tooltipText;
    }

    return (
      <div className="action-buttons">
        <button
          type="button"
          className="action-btn edit-btn"
          disabled={!!isLinked || !hasUpdatePermission}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`../edit-device/${row._id}`);
          }}
          title={editTooltip}>
          <EditPencilCard />
        </button>
        <button
          type="button"
          className="action-btn delete-btn"
          disabled={!!isLinked || !hasDeletePermission}
          onClick={(event) => {
            event.stopPropagation();
            onDeleteClick(row._id);
          }}
          title={deleteTooltip}>
          <DeleteIcon />
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (!disablePageTitle) {
      dispatch(
        setPageTitle({
          title: 'EV Device Hub',
          icon: <ChargerIcon />,
          count: totalResults,
        }),
      );
    }

    dispatch(fetchDeviceDetails());
  }, [dispatch, totalResults, disablePageTitle]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchAllDevices({
        page: DefaultApiLimit.page,
        limit,
        start: '',
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );

    if (isFilterUpdated) {
      dispatch(disableDeviceHubFilterApplied());
    }
  }, [filters, isFilterUpdated, dispatch, limit, sortBy, sortField]);

  /**
   * Handles sort change
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    dispatch(
      fetchAllDevices({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handle page and limit change for pagination
   * @param {any} value - object containing new page and/or limit
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setPage(value.page));
    if (value.limit !== undefined) dispatch(setLimit(value.limit));
    dispatch(
      fetchAllDevices({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllDevices({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Function to auto refresh data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshDevices({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  // Deduplicate devices case-insensitively to avoid duplicate rows in the table
  const uniqueDevices = React.useMemo(() => {
    const map = new Map<string, any>();
    devices.forEach((d) => {
      const key = d.serialNumber?.toLowerCase();
      if (key && !map.has(key)) {
        map.set(key, d);
      }
    });
    return Array.from(map.values());
  }, [devices]);

  const columns: any[] = [
    {
      label: 'Brand Name & Model',
      accessorKey: 'brandName',
      customData: renderBrandModel,
      sortAllow: true,
    },
    {
      label: 'Device Id',
      accessorKey: 'serialNumber',
      headingDetails: true,
      headingDescription: (
        <span>
          Unique identifier assigned to the EV charger within the Device Registry platform.
        </span>
      ),
    },
    {
      label: 'Current Type',
      accessorKey: 'deviceType',
      customData: renderType,
      columnWidth: 100,
      headingDetails: true,
      headingDescription: <span>Indicates whether the charger supports AC or DC charging.</span>,
    },
    // {
    //   label: 'Brand Status',
    //   accessorKey: 'brandStatus',
    //   customData: renderBrandStatus,
    // },
    {
      label: 'Connectors',
      accessorKey: 'connectorInformation',
      sidePanelComponent: <ConnectorSidePanel fetchIdFrom="queryParams" />,
      queryParamsKey: 'connectorDeviceId',
      headingDetails: true,
      headingDescription: (
        <span>
          Lists the charging connector standards available on the charger, such as CCS2, Type 2,
          CHAdeMO, or GB/T.
        </span>
      ),
      /**
       * Renders the connectors cell
       * @param {any} row - Table row data
       * @returns {any} JSX element
       */
      customData: (row: EventDeviceData) => (
        <div
          className="connectors-cell"
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              const parameters = new URLSearchParams(searchParameters);
              parameters.set('connectorDeviceId', row.serialNumber);
              setSearchParameters(parameters);
            }
          }}
          onClick={(event) => {
            event.stopPropagation();
            const parameters = new URLSearchParams(searchParameters);
            parameters.set('connectorDeviceId', row.serialNumber);
            setSearchParameters(parameters);
          }}>
          <span className="connector-count hyperlink">
            {`${row.connectorInformation?.length || 0} Connector${row.connectorInformation?.length > 1 ? 's' : ''}`}
          </span>
        </div>
      ),
    },
    {
      label: 'Protocol',
      accessorKey: 'protocolSupport',
      customData: renderProtocol,
      headingDetails: true,
      headingDescription: (
        <span>
          Charging communication protocol and version supported by the device, such as OCPP 1.6J or
          OCPP 2.0.1.
        </span>
      ),
    },
    {
      label: 'Registration status',
      accessorKey: 'accessStatus',
      customData: renderAccessStatus,
      headingDetails: true,
      headingDescription: (
        <span>Shows whether the charger has been successfully registered in the platform.</span>
      ),
      columnWidth: 150,
    },
    {
      label: 'Connection Status',
      accessorKey: 'connectionStatus',
      customData: renderConnectionStatus,
      headingDetails: true,
      headingDescription: (
        <span>
          Displays the real-time connectivity state of the charger with the central management
          system.
        </span>
      ),
      columnWidth: 150,
    },
    {
      label: 'Attached Status',
      accessorKey: 'attachedAt',
      customData: renderAttachedStatus,
      headingDetails: true,
      headingDescription: (
        <span>
          Indicates whether the charger is currently linked to a site, station, tenant, or operator
          entity.
        </span>
      ),
      // columnWidth: 150,
      sortAllow: true,
    },

    {
      label: 'Attached entity',
      accessorKey: 'deviceOwner',
      customData: renderTenant,
      headingDetails: true,
      headingDescription: (
        <span>Displays the operator or entity currently associated with the charger.</span>
      ),
    },

    {
      label: 'Action',
      accessorKey: '_id',
      sticky: 'right' as const,
      customData: renderActions,
      headingDetails: true,
      headingDescription: (
        <span>Delete or edit is available only when no Associator are linked to this Device.</span>
      ),
    },
  ];

  return (
    <div className="ev-device-hub-wrap">
      <div className="ev-device-hub-container">
        {/* Stats summary bar */}
        {!hideAddButton && (
          <div className="stats-bar">
            <div className="stat-item">
              <span className="stat-label">Total Device</span>
              <span className="stat-value">{deviceStats.totalDevices || 0}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-label">Attached:</span>
              <span className="stat-value">{deviceStats.cpoAssociatedCount || 0}</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-label">Not Attached:</span>
              <span className="stat-value">{deviceStats.nonAssociatedCount || 0}</span>
            </div>
            <div className="stat-divider" />
            <span className="stat-badge ac-badge">
              <span className="badge-dot" />
              AC {deviceStats.acDeviceCount || 0}
            </span>
            <span className="stat-badge dc-badge">
              <span className="badge-dot" />
              DC {deviceStats.dcDeviceCount || 0}
            </span>
            <span className="stat-badge hybrid-badge">
              <span className="badge-dot" />
              HYBRID {deviceStats.hybridDeviceCount || 0}
            </span>
          </div>
        )}

        {/* Main Table */}
        <div>
          <CustomTable
            // dataKey="EventDeviceHub"
            version="v2"
            header={columns}
            data={uniqueDevices}
            serialNo
            loading={loading}
            totalResults={totalResults}
            limit={limit}
            page={page}
            height={339}
            totalPages={totalPages}
            onHiddenColumnsChange={() => {}}
            sizeChangeButtonRequired
            pageLimitChange={handlePageLimitChange}
            sortData={{ sortBy, sortField }}
            handleSortChange={handleSortFunction}
            filters={filters}
            filtersToDispatch={updateDeviceHubFilters}
            actionButton
            refreshRequired
            handleRefresh={handleRefresh}
            hoverLabel="Refresh"
            refershCurrentPages={handleRefresh}
            rowClickEvent={(id) => navigate(`../device-details/${id}`)}
            rowClickParamsKey="_id"
            rightTopContent={
              <div className="table-bottom-button-list">
                {!hideAddButton && permissionCheck('devicesEvDeviceHubAdd') && (
                  <div className="table-button-item">
                    <MyButton
                      label="Add Device"
                      buttonType="submit"
                      onClickFunc={handleAddDevice}
                      svgIcon={<Plus />}
                    />
                  </div>
                )}
              </div>
            }
            requiredRefreshToggle
            refreshFunction={handleAutoRefresh}
            sidePanelTopLeftContent={<div className="csp-header">Connectors</div>}
            filterListLoading={filterListLoading}
            filterListToDispatch={fetchDeviceHubFilters}
            actionDropdown
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ModalBox
        open={showDeleteModal}
        closeModal={handleCancelDelete}
        title={null}
        header={false}
        content={
          <DeleteWarning
            selectedRowId={selectedDeviceId}
            title="Delete Device"
            description="Are you sure you want to permanently delete this device? Once deleted, this action cannot be undone."
            cancelButtonOnClick={handleCancelDelete}
            submitButtonOnClick={handleConfirmDelete}
          />
        }
      />

      {/* Success Notification */}
      {showSuccessNotification && (
        <ModalBox
          open={showSuccessNotification}
          closeModal={() => setShowSuccessNotification(false)}
          title={null}
          header={false}
          notification
          content={
            <SuccessNotification
              title="Success"
              description={notificationMessage}
              onNotificationClose={() => setShowSuccessNotification(false)}
            />
          }
        />
      )}
    </div>
  );
}

EventDeviceHub.defaultProps = {
  hideAddButton: false,
  disablePageTitle: false,
};

export default EventDeviceHub;
